import { useQuery } from '@tanstack/react-query'

import { getSearchCalendarEvents } from 'api/recommendations'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'

/**
 * Static on purpose. The feed ignores place filters and its copy is
 * untranslated, so the payload is identical for every user and one cache entry
 * serves every search widget mounted on the page.
 */
const SEARCH_CALENDAR_EVENTS_QUERY_KEY = ['search-calendar-events'] as const

/** Mirrors the endpoint's own `cache-control: max-age=600`. */
const SEARCH_CALENDAR_EVENTS_STALE_TIME_MS = 10 * 60 * 1000

/**
 * Deliberately longer than the stale time. React Query's five-minute default
 * would collect the payload shortly after the date picker closes, so a user
 * reopening it later in the same session would pay the round trip again.
 */
const SEARCH_CALENDAR_EVENTS_CACHE_TIME_MS = 60 * 60 * 1000

/** Every downstream `useMemo` keys off this array, so it cannot be rebuilt per render. */
const NO_EVENTS: ReadonlyArray<SearchCalendarEvent> = []

/**
 * Calendar events for the hotel search date picker overlay.
 *
 * `isEnabled` should track whether the picker is actually open; the payload is
 * too large to pull on every page that renders a search bar.
 */
function useSearchCalendarEvents(isEnabled: boolean): ReadonlyArray<SearchCalendarEvent> {
  const { data } = useQuery({
    queryKey: SEARCH_CALENDAR_EVENTS_QUERY_KEY,
    queryFn: getSearchCalendarEvents,
    enabled: isEnabled,
    staleTime: SEARCH_CALENDAR_EVENTS_STALE_TIME_MS,
    cacheTime: SEARCH_CALENDAR_EVENTS_CACHE_TIME_MS,
  })

  return data ?? NO_EVENTS
}

export default useSearchCalendarEvents
